import { assertNever } from '../assert.js';
import { isTerminal } from '../runs/run.js';
import { canBeGoneOutside, scanSaysGone } from './outside.js';
/**
 * A deployment, derived from the runs against it.
 *
 * Not a stored record. A deployment *is* the sequence of runs carrying one
 * `deployment_id`, so deriving it means there is nothing to keep consistent: a
 * second table would have to be updated by the same events that write runs, and
 * the interesting failure is the two disagreeing about whether an instance is
 * live. The cost is reading a tenant's runs to answer, which is bounded by the
 * partition and cheap at this scale.
 *
 * It also gives the history criterion for free — who did what, when, and which
 * version *is* the run list, rather than a changelog written alongside it that
 * could omit an entry.
 */
/**
 * What state an instance is in.
 *
 * Five, because a support engineer asked "is this customer's instance up" needs
 * to distinguish "yes", "not yet", "no and here is why", "it is being worked on
 * right now" and "it was deliberately taken down". Collapsing any pair of those
 * produces the answer that sends someone to look in the wrong place.
 *
 * Every state has a distinct word, so a list can encode it in text and not only
 * in colour.
 */
export const DEPLOYMENT_STATUSES = [
    'in-progress',
    'live',
    'apply-failed',
    'destroyed',
    'never-applied',
];
/** Runs that change infrastructure. A plan or an output changes nothing. */
const MUTATES = new Set(['apply', 'destroy']);
const byNewestFirst = (runs) => [...runs].sort((a, b) => (a.requestedAt < b.requestedAt ? 1 : -1));
const statusOf = (runs) => {
    // Newest first, so the first match in each pass is the most recent.
    const newest = byNewestFirst(runs);
    // An in-flight run wins over any history: what someone needs to know first is
    // that the instance is being changed right now.
    if (newest.some((run) => !isTerminal(run.state)))
        return 'in-progress';
    const lastChange = newest.find((run) => MUTATES.has(run.command));
    if (lastChange === undefined)
        return 'never-applied';
    if (lastChange.command === 'destroy') {
        // A failed destroy leaves the instance up, and possibly half torn down. That
        // is not 'destroyed', and calling it so would strand real infrastructure
        // nobody is looking at.
        return lastChange.state === 'succeeded' ? 'destroyed' : 'apply-failed';
    }
    return lastChange.state === 'succeeded' ? 'live' : 'apply-failed';
};
/**
 * When the current life of this instance began, if it has one.
 *
 * Walks oldest-first, unlike everything else here, because the question is
 * where the current life *started* rather than what happened most recently.
 *
 * The timestamp is the apply's `finishedAt` where it has one: an instance is
 * live when its apply finishes, not when it was asked for. Older records
 * predate that field, so `requestedAt` stands in — the difference is at most one
 * apply, against ages measured in months, and a record with no answer at all
 * would be worse.
 */
export const liveSinceOf = (runs) => {
    const oldestFirst = [...runs].sort((a, b) => (a.requestedAt < b.requestedAt ? -1 : 1));
    // The last succeeded destroy ends the previous life. A failed one does not:
    // the instance is still there, possibly half torn down, and its life never
    // ended — which is the same distinction `statusOf` draws one function up.
    let afterDestroy = 0;
    oldestFirst.forEach((run, index) => {
        if (run.command === 'destroy' && run.state === 'succeeded')
            afterDestroy = index + 1;
    });
    const began = oldestFirst
        .slice(afterDestroy)
        .find((run) => run.command === 'apply' && run.state === 'succeeded');
    return began === undefined ? undefined : (began.finishedAt ?? began.requestedAt);
};
const summarize = (deploymentId, runs) => {
    const newest = byNewestFirst(runs);
    const lastRun = newest[0];
    if (lastRun === undefined)
        throw new Error(`no runs for ${deploymentId}`);
    const applied = newest.find((run) => run.command === 'apply' && run.state === 'succeeded');
    const liveSince = liveSinceOf(runs);
    const region = newest.find((run) => run.region !== undefined)?.region;
    const variables = newest.find((run) => run.variables !== undefined && Object.keys(run.variables).length > 0)?.variables;
    /*
     * The newest run that recorded one, like `variables` above.
     *
     * A run started before IM-139 recorded none, so an instance whose recent runs
     * all predate it falls back to the convention at dispatch. Reading the newest
     * that has one rather than the newest full stop means a `plan` that carried
     * it still answers for an `output` run that did not.
     */
    const located = newest.find((run) => run.stateKey !== undefined);
    return {
        deploymentId,
        accountKey: lastRun.accountKey,
        status: statusOf(runs),
        ...(located?.stateKey === undefined ? {} : { stateKey: located.stateKey }),
        ...(located?.stateBucket === undefined ? {} : { stateBucket: located.stateBucket }),
        ...(applied?.chartVersion === undefined ? {} : { chartVersion: applied.chartVersion }),
        ...(region === undefined ? {} : { region }),
        ...(variables === undefined ? {} : { variables }),
        ...(liveSince === undefined ? {} : { liveSince }),
        lastRun,
    };
};
/** The newest apply or destroy started here, in any state. A plan changes nothing. */
const lastChangeOf = (runs) => byNewestFirst(runs).find((run) => MUTATES.has(run.command))?.requestedAt ?? '';
const later = (a, b) => (a ?? '') > (b ?? '') ? (a ?? '') : (b ?? '');
/**
 * Applies a destroyed-outside mark and the scan's evidence to one deployment.
 *
 * The mark first, because it changes the status the evidence is judged
 * against: a marked instance reads `destroyed` and is no longer something the
 * scan should prompt about.
 */
const withOutside = (d, lastChange, evidence) => {
    const key = hiddenKey(d.accountKey, d.deploymentId);
    const mark = evidence.marks?.get(key);
    if (mark !== undefined && mark.at > lastChange) {
        // `liveSince` goes with it: a destroyed instance has no current life, which
        // is what `liveSinceOf` already says after a destroy run here.
        const { liveSince: _ended, ...rest } = d;
        return { ...rest, status: 'destroyed', destroyedOutside: mark };
    }
    if (!canBeGoneOutside(d.status))
        return d;
    const gone = scanSaysGone(evidence.inventories?.get(d.accountKey), d.deploymentId, later(d.lastRun?.requestedAt, d.adopted?.at));
    return gone === undefined ? d : { ...d, scanSaysGone: gone };
};
/**
 * How a hidden instance is identified across the two places that must agree:
 * the derivation here, and the scan in `discovery/inventory.ts`.
 *
 * A stringified tuple rather than an interpolation with a separator, for the
 * reason that file already records: any separator character can also appear in
 * one of the halves, and a collision would hide the wrong instance.
 */
export const hiddenKey = (accountKey, deploymentId) => JSON.stringify([accountKey, deploymentId]);
/**
 * The only two states an instance may be hidden in.
 *
 * `apply-failed` is deliberately absent. That instance may be half-built and
 * still costing money, and this module already refuses to call a failed destroy
 * `destroyed` for the same reason — hiding it removes it from the one list that
 * exists to surface exactly that. `live` and `in-progress` are obvious.
 */
export const HIDEABLE_STATUSES = ['destroyed', 'never-applied'];
/**
 * Takes a `string`, not a `DeploymentStatus`.
 *
 * The console reads a status off the wire as a plain string — deliberately, so
 * a status this build does not recognise renders raw rather than as "Unknown".
 * Requiring the narrow type here would push a cast into every caller, and a
 * cast is exactly how an unrecognised status would end up treated as hideable.
 * Membership answers the question for both.
 */
export const isHideable = (status) => HIDEABLE_STATUSES.includes(status);
/**
 * Whether a command makes sense against an instance in this status.
 *
 * Only the two commands that act on an instance that already exists are
 * narrowed. A plan or an apply is how an instance comes into being, so neither
 * needs one.
 *
 * - **`output` needs `live`.** It reads the endpoints an instance serves on,
 *   and anything else has none worth reading. A destroyed one still carries the
 *   last banner, which describes something that has gone.
 * - **`destroy` needs `live` or `apply-failed`.** The second is not a
 *   concession: a failed apply may be half-built and billing, and a failed
 *   destroy reads `apply-failed` too, so refusing it would leave nothing in the
 *   console able to finish either teardown. `in-progress` waits for the run
 *   already going; `destroyed` and `never-applied` have nothing to destroy.
 *
 * `undefined` is no instance at all — an id nothing has run against — and
 * permits neither, which is the API's own refusal seen from the form. Takes a
 * `string` for the reason `isHideable` does: an unrecognised status permits
 * nothing rather than being cast into something that does.
 *
 * Not a control. The API decides what runs; this decides what is offered.
 */
export const commandSuitsStatus = (command, status) => {
    switch (command) {
        case 'plan':
        case 'apply':
            return true;
        case 'output':
            return status === 'live';
        case 'destroy':
            return status === 'live' || status === 'apply-failed';
        default:
            return assertNever(command, 'run command');
    }
};
/**
 * An adopted instance, as a deployment, before anything has been run against it.
 *
 * **`live`, and that is a decision rather than a derivation** (IM-140). Nothing
 * here observed an apply — there are no runs to observe — so the status is the
 * customer's assertion that the instance exists, made by uploading the
 * config.json it was deployed with. The alternative was a sixth status meaning
 * "brought in, nothing run here yet", and it was rejected: an adopted instance
 * should behave exactly like one this control plane deployed, and a status of
 * its own would have every list, filter and colour carry the distinction
 * forever for the sake of its first hour.
 *
 * What it must not claim is history. `lastRun` is absent, not invented.
 */
const fromAdoption = (adoption) => ({
    deploymentId: adoption.deploymentId,
    accountKey: adoption.accountKey,
    status: 'live',
    region: adoption.region,
    stateBucket: adoption.stateBucket,
    stateKey: adoption.stateKey,
    adopted: { at: adoption.adoptedAt, by: adoption.adoptedBy },
    ...(adoption.chartVersion === undefined ? {} : { chartVersion: adoption.chartVersion }),
    /*
     * What it was deployed with, from the config.json it was brought in with.
     *
     * This is the prefill for the first run against it, and the best one that
     * will ever exist: the values it is actually running. Without it that form is
     * blank and everything is retyped, which is the gap IM-117 closed for runs.
     * Secrets were removed in the browser before any of it was sent.
     */
    ...(adoption.variables === undefined ? {} : { variables: adoption.variables }),
});
/**
 * When this instance was last touched, for ordering.
 *
 * An adopted instance has no run, so it sorts by when it was brought in — which
 * is the last thing that happened to it here, and is the honest answer. The
 * empty string sorts last, which is where a record with neither belongs.
 */
const touchedAt = (d) => d.lastRun?.requestedAt ?? d.adopted?.at ?? '';
/**
 * Every deployment, minus the hidden ones.
 *
 * The filter is a parameter rather than something the caller applies afterwards
 * so that forgetting it is visible at the call site. It defaults to empty,
 * which is what every caller that does not care about hiding wants.
 */
export const deploymentsFrom = (runs, hidden = new Set(), 
/**
 * The marks themselves, for a caller that asked to *see* hidden rows.
 *
 * Two parameters rather than one because they answer different questions:
 * `hidden` is what to leave out, `marks` is how to label what is left in.
 * A caller showing hidden rows passes an empty `hidden` and a full `marks`.
 */
marks = new Map(), 
/**
 * Recorded lifetimes, keyed like the marks above (IM-134).
 *
 * A parameter rather than something the caller merges afterwards, for the
 * same reason `hidden` is: forgetting it is then visible at the call site.
 * Defaults to empty, which is every caller that does not care.
 */
lifetimes = new Map(), 
/**
 * Instances brought in rather than deployed here (IM-140).
 *
 * A parameter for the same reason the three above are: an adopted instance
 * has **no runs**, so a derivation over runs alone cannot see it and it was
 * absent from every list in the product — adoption recorded where the state
 * lived and no screen ever consulted it. Forgetting to pass these is then
 * visible at the call site rather than as an instance nobody can find.
 */
adoptions = [], 
/**
 * Destroyed-outside marks and the kept inventories (IM-156). Every caller
 * that shows a status or decides on one passes these; a caller that only
 * asks whether an id exists does not need to.
 */
outside = {}) => {
    const grouped = new Map();
    for (const run of runs) {
        if (run.deploymentId === undefined || run.deploymentId === '')
            continue;
        const existing = grouped.get(run.deploymentId);
        if (existing === undefined)
            grouped.set(run.deploymentId, [run]);
        else
            existing.push(run);
    }
    const adopted = new Map(adoptions.map((a) => [hiddenKey(a.accountKey, a.deploymentId), a]));
    /*
     * An adoption with runs is a deployment; an adoption without runs is one too.
     *
     * The first case is the ordinary one after somebody has run anything against
     * an adopted instance, and the runs answer everything except where the state
     * lives. The second is the state it arrives in, and is what made it invisible.
     */
    const derived = [...grouped.entries()].map(([deploymentId, its]) => summarize(deploymentId, its));
    const lastChange = new Map([...grouped.entries()].map(([deploymentId, its]) => [
        hiddenKey(its[0]?.accountKey ?? '', deploymentId),
        lastChangeOf(its),
    ]));
    const seen = new Set(derived.map((d) => hiddenKey(d.accountKey, d.deploymentId)));
    return [
        ...derived,
        ...adoptions
            .filter((a) => !seen.has(hiddenKey(a.accountKey, a.deploymentId)))
            .map(fromAdoption),
    ]
        .filter((d) => !hidden.has(hiddenKey(d.accountKey, d.deploymentId)))
        .map((d) => {
        const key = hiddenKey(d.accountKey, d.deploymentId);
        const mark = marks.get(key);
        const lifetime = lifetimes.get(key);
        const from = adopted.get(key);
        return {
            ...d,
            ...(mark === undefined ? {} : { hidden: mark }),
            ...(lifetime === undefined ? {} : { lifetime }),
            /*
             * The adoption's state location wins over anything derived.
             *
             * Its key is the one the installer actually used, read out of the
             * config.json the instance was deployed with; a key derived from the
             * deployment id is this control plane's convention, and for an adopted
             * instance the convention is exactly what does not hold.
             */
            ...(from === undefined
                ? {}
                : {
                    adopted: { at: from.adoptedAt, by: from.adoptedBy },
                    stateBucket: from.stateBucket,
                    stateKey: from.stateKey,
                    /*
                     * The config.json's variables, but only until a run has its own.
                     *
                     * A run recorded here is newer than the file that was uploaded,
                     * and it is what this instance was last *asked* to deploy. The
                     * uploaded file describes the state it arrived in, which stops
                     * being the best answer the moment somebody runs something.
                     */
                    ...(d.variables !== undefined || from.variables === undefined
                        ? {}
                        : { variables: from.variables }),
                }),
        };
    })
        .map((d) => withOutside(d, 
    // An adoption is a change too: a mark made before somebody brought the
    // instance in describes something that has since been claimed.
    later(lastChange.get(hiddenKey(d.accountKey, d.deploymentId)), d.adopted?.at), outside))
        .sort((a, b) => (touchedAt(a) < touchedAt(b) ? 1 : -1));
};
/** One deployment with its full history, or undefined if nothing named it. */
/**
 * One deployment, hidden or not.
 *
 * Deliberately *not* filtered: a hidden instance's own screen stays reachable
 * and says it is hidden. The runs are still there and the audit trail still
 * references it, so a support engineer following a link from a ticket should
 * land on the record rather than a 404 that looks like a typo. Hidden means
 * absent from the lists, which is what was asked for.
 */
export const deploymentFrom = (runs, deploymentId, hidden, lifetime, 
/**
 * The adoption, when this instance was brought in rather than deployed here.
 *
 * Without it an adopted instance is a **404 on its own screen while appearing
 * in every list** — which is exactly what shipped: `deploymentsFrom` learned
 * about adoptions and this did not, so a row people could see was a link they
 * could not follow. The two derivations have to agree about what exists.
 */
adoption, 
/** The same evidence the list is given, so the two cannot disagree (IM-156). */
outside = {}) => {
    const its = runs.filter((run) => run.deploymentId === deploymentId);
    if (its.length === 0) {
        // Brought in and never run here. Its history is empty rather than absent:
        // there is nothing to show, and that is the honest thing to show.
        if (adoption === undefined)
            return undefined;
        return withOutside({
            ...fromAdoption(adoption),
            ...(hidden === undefined ? {} : { hidden }),
            ...(lifetime === undefined ? {} : { lifetime }),
            history: [],
        }, adoption.adoptedAt, outside);
    }
    const history = byNewestFirst(its);
    // The newest run that has any, not the newest run: an `output` run reads the
    // banner without changing anything, so the freshest endpoints often belong to
    // a run that is not the last thing that happened.
    const outputs = history.find((run) => run.outputs !== undefined)?.outputs;
    const summary = summarize(deploymentId, its);
    const detail = {
        ...(hidden === undefined ? {} : { hidden }),
        ...(lifetime === undefined ? {} : { lifetime }),
        ...summary,
        /*
         * An adopted instance keeps its own state location and provenance once it
         * has runs, exactly as in the list. The run's variables win over the
         * uploaded file's, which `summarize` has already decided.
         */
        ...(adoption === undefined
            ? {}
            : {
                adopted: { at: adoption.adoptedAt, by: adoption.adoptedBy },
                stateBucket: adoption.stateBucket,
                stateKey: adoption.stateKey,
                ...(summary.variables !== undefined || adoption.variables === undefined
                    ? {}
                    : { variables: adoption.variables }),
            }),
        history,
        ...(outputs === undefined ? {} : { outputs }),
    };
    return withOutside(detail, later(lastChangeOf(its), adoption?.adoptedAt), outside);
};
//# sourceMappingURL=deployment.js.map